import React, { useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Switch,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { StyledTextField, StyledButton } from "./StyledComponents";
import { useSearchContext } from "../context/SearchContext";
import { getStopsLabel, getStopsFilterKey, decimalToTimeString } from "../utils/filterUtils";
import { calculateAllBagPrices, formatPrice, getLayoverAirportInfo } from "../utils/flightDataUtils";
import TimeRangeSlider from "./TimeRangeSlider";
import DurationRangeSlider from "./DurationRangeSlider";
import PriceRangeSlider from "./PriceRangeSlider";

const containerStyle = {
  marginBottom: 12,
  borderBottom: '1px solid #e0e0e0'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20
};

const titleStyle = {
  fontWeight: 600,
  fontSize: 16,
  margin: 0,
  color: '#222'
};

const resetStyle = {
  color: '#008cff',
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 600,
  background: 'none',
  border: 'none',
  padding: 0
};

const labelStyle = {
  marginBottom: 2,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer'
};

const checkboxStyle = {
  appearance: 'none',
  width: 18,
  height: 18,
  marginRight: 12,
  border: '1.5px solid #d1d5db',
  borderRadius: '3px',
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: '#fff',
  outline: 'none',
};

const checkedCheckboxStyle = {
  ...checkboxStyle,
  backgroundColor: '#fff',
  borderColor: '#d1d5db',
};

// Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange, disabled = false }) => (
  <div 
    style={{
      ...(checked ? checkedCheckboxStyle : checkboxStyle),
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      backgroundColor: disabled ? '#f5f5f5' : (checked ? '#fff' : '#fff'),
      borderColor: disabled ? '#ddd' : (checked ? '#d1d5db' : '#d1d5db')
    }}
    onClick={disabled ? undefined : onChange}
  >
    {checked && (
      <svg
        style={{
          position: 'absolute',
          top: '2px',
          left: '3px',
          width: '10px',
          height: '10px',
          fill: disabled ? '#ccc' : '#666'
        }}
        viewBox="0 0 24 24"
      >
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
      </svg>
    )}
  </div>
);

const labelContentStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center'
};

const labelTextStyle = {
  fontSize: 13,
  fontWeight: 400
};

const priceStyle = {
  color: '#222',
  fontSize: 13,
  fontWeight: 500,
  marginLeft: 'auto'
};

function FiltersSidebar() {
  const {
    trackPrices, setTrackPrices,
    smartFilterText, setSmartFilterText,
    carryOnCount, setCarryOnCount,
    checkedBagCount, setCheckedBagCount,
    bookOnKayak, setBookOnKayak,
    timeFilter, setTimeFilter,
    timeRanges, setTimeRanges,
    durationRanges, setDurationRanges,
    stopsFilter, setStopsFilter,
    airlinesFilter, setAirlinesFilter,
    allianceFilter, setAllianceFilter,
    airportsFilter, setAirportsFilter,
    cabinFilter, setCabinFilter,
    priceRange, setPriceRange,
    amenitiesFilter, setAmenitiesFilter,
    flightQuality, setFlightQuality,
    availableFilters,
    filteredResults,
    searchResults,
    layoverAirportsFilter, setLayoverAirportsFilter,
    // Trip configuration
    tripType,
  } = useSearchContext();
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isCabinOpen, setIsCabinOpen] = useState(false);
  const [isLayoverAirportsOpen, setIsLayoverAirportsOpen] = useState(false);
  const [isFlightQualityOpen, setIsFlightQualityOpen] = useState(false);
  const [isAircraftOpen, setIsAircraftOpen] = useState(false);
  const [isBookingSitesOpen, setIsBookingSitesOpen] = useState(false);
  const handleStopsFilter = (value) => {
    if (stopsFilter.includes(value)) {
      setStopsFilter(stopsFilter.filter(item => item !== value));
    } else {
      setStopsFilter([...stopsFilter, value]);
    }
  };

  const handleAirlinesFilter = (airline) => {
    if (airlinesFilter.includes(airline)) {
      setAirlinesFilter(airlinesFilter.filter(item => item !== airline));
    } else {
      setAirlinesFilter([...airlinesFilter, airline]);
    }
  };

  const handleCabinFilter = (cabin) => {
    if (cabinFilter.includes(cabin)) {
      setCabinFilter(cabinFilter.filter(item => item !== cabin));
    } else {
      setCabinFilter([...cabinFilter, cabin]);
    }
  };

  const handleAmenitiesFilter = (amenity) => {
    if (amenitiesFilter.includes(amenity)) {
      setAmenitiesFilter(amenitiesFilter.filter(item => item !== amenity));
    } else {
      setAmenitiesFilter([...amenitiesFilter, amenity]);
    }
  };

  const handleLayoverAirportsFilter = (airportCode) => {
    if (layoverAirportsFilter.includes(airportCode)) {
      setLayoverAirportsFilter(layoverAirportsFilter.filter(item => item !== airportCode));
    } else {
      setLayoverAirportsFilter([...layoverAirportsFilter, airportCode]);
    }
  };

  // Time range handlers
  const handleATLTimeChange = (newRange) => {
    setTimeRanges(prev => ({
      ...prev,
      atl: newRange
    }));
  };

  const handleABQTimeChange = (newRange) => {
    setTimeRanges(prev => ({
      ...prev,
      abq: newRange
    }));
  };

  const resetTimeFilters = () => {
    setTimeRanges({
      atl: { start: 8.62, end: 13.92 },
      abq: { start: 7.93, end: 17.02 }
    });
  };

  // Duration range handlers
  const handleFlightLegDurationChange = (newRange) => {
    setDurationRanges(prev => ({
      ...prev,
      flightLeg: newRange
    }));
  };

  const handleLayoverDurationChange = (newRange) => {
    setDurationRanges(prev => ({
      ...prev,
      layover: newRange
    }));
  };

  const resetDurationFilters = () => {
    setDurationRanges({
      // Reset with start 0 to include all by default; slider shows 1h20m as min label only
      flightLeg: { start: 0, end: 1677 },
      layover: { start: 35, end: 1424 }     // 0h 35m to 23h 44m
    });
  };

  // Price range handler
  const handlePriceRangeChange = (newRange) => {
    setPriceRange(newRange);
  };

  // Build a map of airline code -> full name from current results
  const airlineNameByCode = React.useMemo(() => {
    const map = {};
    if (!Array.isArray(searchResults)) return map;
    for (const res of searchResults) {
      const outCode = res?.outbound?.airline;
      const outName = res?.outbound?.airlineName;
      if (outCode && outName) map[outCode] = outName;
      if (Array.isArray(res?.returnFlights)) {
        for (const rf of res.returnFlights) {
          const rCode = rf?.airline;
          const rName = rf?.airlineName;
          if (rCode && rName) map[rCode] = rName;
        }
      }
    }
    return map;
  }, [searchResults]);

  const getAirlineName = (code) => airlineNameByCode[code] || code;

  // Reset specific filters
  const resetStopsFilter = () => setStopsFilter([]);
  const resetAirlinesFilter = () => setAirlinesFilter([]);
  
  // Select/clear all airlines
  const selectAllAirlines = () => setAirlinesFilter([...availableFilters.airlines]);
  const clearAllAirlines = () => setAirlinesFilter([]);

  const [showMoreAirlines, setShowMoreAirlines] = useState(false);
  // Airports: depart/return same toggle
  const [sameAirport, setSameAirport] = useState(false);

  // Ensure landing toggle is not shown/used for One-way trips
  React.useEffect(() => {
    if (tripType === "One-way" && timeFilter !== "takeoff") {
      setTimeFilter("takeoff");
    }
  }, [tripType, timeFilter, setTimeFilter]);

  // Airport groups to display under the Airports section
  const airportGroups = {
    'San Francisco': [
      { code: 'SFO', name: 'San Francisco' },
      { code: 'OAK', name: 'Oakland' },
      { code: 'SJC', name: 'San Jose' },
    ],
    'Los Angeles': [
      { code: 'LAX', name: 'Los Angeles' },
      { code: 'ONT', name: 'Ontario' },
      { code: 'BUR', name: 'Bob Hope' },
      { code: 'SNA', name: 'J. Wayne/Orange' },
    ],
  };

  // Compute min price for an airport code from current search results
  const getAirportMinPrice = (airportCode) => {
    if (!Array.isArray(searchResults) || searchResults.length === 0) return null;
    let min = null;
    for (const result of searchResults) {
      const pricesToConsider = [];
      if (result?.outbound) {
        const dep = result.outbound?.departure?.airport;
        const arr = result.outbound?.arrival?.airport;
        if (dep === airportCode || arr === airportCode) {
          const p = result.totalPrice || result.price?.economy || result.outbound?.price?.economy;
          if (typeof p === 'number') pricesToConsider.push(p);
        }
      }
      if (Array.isArray(result?.returnFlights)) {
        for (const rf of result.returnFlights) {
          const dep = rf?.departure?.airport;
          const arr = rf?.arrival?.airport;
          if (dep === airportCode || arr === airportCode) {
            const p = rf?.price?.economy;
            if (typeof p === 'number') pricesToConsider.push(p);
          }
        }
      }
      for (const p of pricesToConsider) {
        if (min == null || p < min) min = p;
      }
    }
    return min;
  };

  // Determine which airports actually appear in the current search results
  const availableAirportCodes = React.useMemo(() => {
    const codes = new Set();
    if (!Array.isArray(searchResults)) return codes;
    for (const result of searchResults) {
      const dep = result?.outbound?.departure?.airport;
      const arr = result?.outbound?.arrival?.airport;
      if (dep) codes.add(dep);
      if (arr) codes.add(arr);
      if (Array.isArray(result?.returnFlights)) {
        for (const rf of result.returnFlights) {
          const rDep = rf?.departure?.airport;
          const rArr = rf?.arrival?.airport;
          if (rDep) codes.add(rDep);
          if (rArr) codes.add(rArr);
        }
      }
    }
    return codes;
  }, [searchResults]);

  // Small reusable row that shows a hover "only" affordance
  const FilterRow = ({ checked, disabled = false, onToggle, onOnly, children, price = null, textStyle = {}, showInlineOnly = false, hideOnly = false, rowStyle = {} }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <label
        style={{
          ...labelStyle,
          ...rowStyle,
          opacity: disabled ? 0.9 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={disabled ? undefined : onToggle}
      >
        <CustomCheckbox checked={checked} onChange={disabled ? undefined : onToggle} disabled={disabled} />
        <div style={{ ...labelContentStyle, gap: 8 }}>
          <span style={{ ...labelTextStyle, ...textStyle, color: disabled ? '#bbb' : '#222' }}>
            {children}
            {showInlineOnly && hovered && !disabled && !hideOnly && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOnly && onOnly();
                }}
                style={{ marginLeft: 8, fontWeight: 700, textDecoration: 'underline', cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#bbb' : '#222', fontSize: 12 }}
              >
                only
              </span>
            )}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            {!disabled && hovered && !showInlineOnly && !hideOnly && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOnly && onOnly();
                }}
                style={{ color: '#222', fontSize: 12 }}
              >
                <span style={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>only</span>
              </span>
            )}
            {price != null && (
              <span style={{ ...priceStyle, color: disabled ? '#bbb' : '#222' }}>${price}</span>
            )}
          </div>
        </div>
      </label>
    );
  };

  // Compute minimum total price that includes a given airline
  const getAirlineMinTotalPrice = (airlineCode) => {
    if (!Array.isArray(searchResults) || searchResults.length === 0) return null;
    let min = null;
    for (const result of searchResults) {
      const appearsInOutbound = result?.outbound?.airline === airlineCode;
      let appearsInReturn = false;
      if (Array.isArray(result?.returnFlights)) {
        appearsInReturn = result.returnFlights.some(rf => rf?.airline === airlineCode);
      }
      if (appearsInOutbound || appearsInReturn) {
        const p = result?.totalPrice;
        if (typeof p === 'number') {
          if (min == null || p < min) min = p;
        }
      }
    }
    return min;
  };

  // Determine if any itinerary is operated by multiple airlines (for UI-only disabled row)
  const hasMultipleAirlinesOption = React.useMemo(() => {
    if (!Array.isArray(searchResults)) return false;
    return searchResults.some(res => {
      if (!Array.isArray(res?.returnFlights) || res.returnFlights.length === 0) return false;
      const outboundAirline = res?.outbound?.airline;
      return res.returnFlights.some(rf => rf?.airline && rf.airline !== outboundAirline);
    });
  }, [searchResults]);

  return (
    <Box sx={{ width: 220, minWidth: 220, background: 'transparent', marginRight: 3, marginLeft: 5 }}>
      {/* Book now card */}
      <Box sx={{
        color: '#192024',
        border: '1px solid #d9e2e8',
        borderRadius: '8px',
        background: '#fff',
        padding: '12px 16px',
        marginBottom: '16px',
        minHeight: '90px',
      }}>
        {/* Header with icon and title */}
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
          <Box sx={{ 
            marginRight: 1, 
            background: '#ccffe5', 
            borderRadius: '4px', 
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 200 200" width="24px" height="24px" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d="M172 39.5h-40v15h23.795l-52.215 65.269C74.934 99.308 70.4 93.343 64.425 99.982l-45 50l11.149 10.035l40.518-45.021l29.548 21.105c7.933 5.668 7.052 2.538 63.859-68.471V87h15c.001-38.749 2.498-47.5-7.499-47.5zm-7.625 15c.156 0 .125-.026.125.1l-.125-.1z" fill="#008a00"/>
            </svg>
          </Box>
          <Typography variant="h6" sx={{ color: '#008a00', fontWeight: 600, fontSize: 16 }}>
            Book now
          </Typography>
        </Box>
        
        {/* Message with info icon */}
        <Box sx={{ display: 'flex', alignItems: 'end'}}>
          <Typography variant="body2" sx={{ color: '#192024', fontSize: 14, flex: 1 }}>
            Prices are unlikely to drop before your departure.
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              marginLeft: 0.5,
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="16px" height="16px" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path fillRule="evenodd" clipRule="evenodd" d="M100 19c-44.187 0-80 35.813-80 80s35.813 80 80 80s80-35.813 80-80s-35.813-80-80-80zm0 145.455c-36.092 0-65.454-29.363-65.454-65.455S63.908 33.545 100 33.545S165.454 62.909 165.454 99S136.092 164.455 100 164.455zm-8.98-72.284h17.96v55.419H91.02V92.171zm0-35.265h17.96v17.96H91.02v-17.96z" fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Divider */}
        <Divider sx={{ margin: '8px 0' }} />
        
        {/* Track prices section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14 }}>
            Track prices
          </Typography>
          <Switch 
            checked={trackPrices}
            onChange={(e) => setTrackPrices(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase': {
                color: '#ccc',
              },
              '& .MuiSwitch-track': {
                backgroundColor: '#ccc',
              },
              '& .Mui-checked': {
                color: '#008a00',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#008a00',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Smart Filters */}
      <Box sx={{
        color: '#192024',
        border: '1px solid #d9e2e8',
        borderRadius: '8px',
        background: '#fff',
        padding: '9px 16px',
        marginBottom: '16px',
      }}>
        {/* Header with animated icon and title */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* Animated Icon */}   
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ marginRight: 1 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 200 200" role="presentation">
                <path fill="currentColor" d="M 115.231 75.457 C 91.876 71.115 73.612 52.702 69.271 29.498 C 69.12 28.299 67.46 28.299 67.324 29.498 C 62.982 52.851 44.569 71.115 21.366 75.352 C 20.167 75.608 20.167 77.253 21.366 77.404 C 44.718 81.745 62.982 100.159 67.324 123.363 C 67.475 124.56 69.12 124.56 69.271 123.363 C 73.612 100.009 92.026 81.745 115.231 77.404 C 116.428 77.253 116.428 75.756 115.231 75.457 Z">
                  <animate attributeName="opacity" values="1;0;1" keyTimes="0;0.5;1" dur="3s" begin="1.2000000000000002s" repeatCount="indefinite" repeatDur="10s"></animate>
                </path>
                <path fill="currentColor" d="M 131.81 148.07 C 120.11 145.87 110.91 136.67 108.71 124.97 C 108.61 124.37 107.81 124.37 107.71 124.97 C 105.51 136.67 96.31 145.87 84.61 148.07 C 84.01 148.17 84.01 148.97 84.61 149.07 C 96.31 151.26 105.51 160.47 107.71 172.17 C 107.81 172.77 108.61 172.77 108.71 172.17 C 110.91 160.47 120.11 151.27 131.81 149.07 C 132.31 148.97 132.31 148.17 131.81 148.07 Z">
                  <animate attributeName="opacity" values="1;0;1" keyTimes="0;0.5;1" dur="2.7s" begin="0s" repeatCount="indefinite" repeatDur="10s"></animate>
                </path>
                <path fill="currentColor" d="M 179.61 91.87 C 164.01 88.97 151.81 76.67 148.91 61.17 C 148.81 60.37 147.7 60.37 147.61 61.17 C 144.71 76.77 132.41 88.97 116.91 91.8 C 116.11 91.97 116.11 93.07 116.91 93.17 C 132.51 96.07 144.71 108.37 147.61 123.87 C 147.71 124.67 148.81 124.67 148.91 123.87 C 151.81 108.27 164.11 96.07 179.61 93.17 C 180.41 93.07 180.41 92.07 179.61 91.87 Z">
                  <animate attributeName="opacity" values="1;0;1" keyTimes="0;0.5;1" dur="2.0999999999999996s" begin="0.75s" repeatCount="indefinite" repeatDur="10s"></animate>
                </path>
              </svg>
            </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: '#192024' }}>
                  Smart Filters
                </Typography>
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 16, color: '#ff690f' }}>.</Typography>
              </Box>
              {/* Subtitle line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 , marginLeft: 1}}>
                <Typography variant="body2" sx={{ color: '#666', fontSize: 10 }}>
                  BETA • Powered by ChatGPT
                </Typography>
                <IconButton 
                  size="small" 
                  sx={{ 
                    padding: 0, 
                    '&:hover': { background: 'transparent' }
                  }}
                >
                  <svg viewBox="0 0 200 200" width="0.75em" height="1rem" xmlns="http://www.w3.org/2000/svg" role="presentation" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M100 19c-44.187 0-80 35.813-80 80s35.813 80 80 80s80-35.813 80-80s-35.813-80-80-80zm0 145.455c-36.092 0-65.454-29.363-65.454-65.455S63.908 33.545 100 33.545S165.454 62.909 165.454 99S136.092 164.455 100 164.455zm-8.98-72.284h17.96v55.419H91.02V92.171zm0-35.265h17.96v17.96H91.02v-17.96z" fill="#666"/>
                  </svg>
                </IconButton>
              </Box>
            </Box>
          </Box>
          
          {/* Chevron for collapse */}
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d="M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z" fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Collapsible content - hidden by default */}
        <Box sx={{ display: 'none' }}>
          <StyledTextField
            fullWidth
            multiline
            rows={3}
            placeholder="What are you looking for? Try something like: I want to see flights with no layover under $300."
            value={smartFilterText}
            onChange={(e) => setSmartFilterText(e.target.value)}
            sx={{ marginBottom: 1 }}
          />
          <StyledButton
            fullWidth
            variant="outlined"
            sx={{ 
              background: '#e9edf0', 
              color: '#888', 
              border: 'none',
              '&:hover': {
                background: '#d9dde0',
                border: 'none',
              }
            }}
          >
            Filter flights
          </StyledButton>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'flex-start',
        marginBottom: 2,
        padding: '8px 0',
        paddingBottom: 3,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" sx={{ 
          color: '#888', 
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span style={{ fontWeight: 500, color: '#222' }}>{filteredResults.length}</span>
          <span>of</span>
          <span 
            style={{ 
              color: '#008cff', 
              cursor: 'pointer',
              fontWeight: 500
            }}
            onClick={() => console.log('View all flights clicked')}
          >
            {searchResults.length} flights
          </span>
        </Typography>
      </Box>

      {/* Stops Filter */}
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h6 style={titleStyle}>
            Stops
          </h6>
         
        </div>

        {[
          { value: 0, label: 'Nonstop', key: 'nonstop' },
          { value: 1, label: '1 stop', key: '1stop' },
          { value: 2, label: '2+ stops', key: '2stops' }
        ].map(stopOption => {
          const isAvailable = availableFilters.stops.includes(stopOption.value);
          const isChecked = stopsFilter.includes(stopOption.key);
          const isDisabled = !isAvailable;
          const minPrice = availableFilters.stopsPrices[stopOption.value];
          
          return (
            <FilterRow
              key={stopOption.key}
              checked={isChecked}
              disabled={isDisabled}
              onToggle={() => handleStopsFilter(stopOption.key)}
              onOnly={() => setStopsFilter([stopOption.key])}
              price={minPrice}
            textStyle={{ fontSize: 14, fontWeight: 400 , marginBottom: 5 }}
              showInlineOnly
            >
              {stopOption.label}
            </FilterRow>
          );
        })}
      </div>

 {/* Airports */}
 <div style={containerStyle}>
        <div style={headerStyle}>
          <h6 style={titleStyle}>
            Airports
          </h6>
        </div>

        {/* Depart/return same */}
        <FilterRow
          checked={sameAirport}
          onToggle={() => setSameAirport(!sameAirport)}
          hideOnly
        >
          Depart/return same
        </FilterRow>

        {Object.entries(airportGroups).map(([city, airports]) => {
          const visibleAirports = airports.filter(({ code }) => availableAirportCodes.has(code));
          if (visibleAirports.length === 0) return null;
          return (
            <React.Fragment key={city}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 15, margin: '12px 0 4px 0' }}>
                {city}
              </Typography>
              {visibleAirports.map(({ code, name }) => {
                const lower = code.toLowerCase();
                const isChecked = airportsFilter.includes(lower);
                const price = getAirportMinPrice(code);
                return (
                  <FilterRow
                    key={code}
                    checked={isChecked}
                    onToggle={() => {
                      if (isChecked) {
                        setAirportsFilter(airportsFilter.filter(item => item !== lower));
                      } else {
                        setAirportsFilter([...airportsFilter, lower]);
                      }
                    }}
                    onOnly={() => setAirportsFilter([lower])}
                    price={typeof price === 'number' ? price : null}
                    textStyle={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}
                    showInlineOnly
                  >
                    {`${code}: ${name}`}
                  </FilterRow>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>


      {/* Fees assistance */}
      <Box sx={{ marginBottom: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 13 }}>
            Fees assistance
          </Typography>
          <IconButton 
                  size="small" 
                  sx={{ 
                    padding: 1, 
                    '&:hover': { background: 'transparent' }
                  }}
                >
                  <svg viewBox="0 0 200 200" width="1em" height="1rem" xmlns="http://www.w3.org/2000/svg" role="presentation" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M100 19c-44.187 0-80 35.813-80 80s35.813 80 80 80s80-35.813 80-80s-35.813-80-80-80zm0 145.455c-36.092 0-65.454-29.363-65.454-65.455S63.908 33.545 100 33.545S165.454 62.909 165.454 99S136.092 164.455 100 164.455zm-8.98-72.284h17.96v55.419H91.02V92.171zm0-35.265h17.96v17.96H91.02v-17.96z" fill="#666"/>
                  </svg>
                </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          alignContent: 'space-between',
          fontFamily: `'TT Hoves Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
          fontWeight: 400,
          fontSize: 14,
          lineHeight: '20px',
          fontFeatureSettings: '"liga" 0',
          marginBottom: 1.25 
        }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#222' }}>
            <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" className="c8QIE-bag-svg" role="presentation">
              <path d="M130 185a15 15 0 01-14.14-10H84.14a15 15 0 01-28.28 0H55a15 15 0 01-15-15v-60a15 15 0 0115-15h15V27.12C70 22.6 73.9 15 100 15c11.2 0 30 1.58 30 12.12V85h15a15 15 0 0115 15v60a15 15 0 01-15 15h-.86A15 15 0 01130 185zm-50-20h40a5 5 0 015 5a5 5 0 0010 0a5 5 0 015-5h5a5 5 0 005-5v-60a5 5 0 00-5-5H55a5 5 0 00-5 5v60a5 5 0 005 5h5a5 5 0 015 5a5 5 0 0010 0a5 5 0 015-5zm0-80h40V28.1c-2-1.23-8.93-3.1-20-3.1s-18 1.87-20 3.1zm45 60a5 5 0 01-5-5v-20a5 5 0 0110 0v20a5 5 0 01-5 5zm-50 0a5 5 0 01-5-5v-20a5 5 0 0110 0v20a5 5 0 01-5 5z" fill="currentColor"></path>
            </svg>
            <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, lineHeight: '20px' }}>Carry-on bag</Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => setCarryOnCount(Math.max(0, carryOnCount - 1))}
            sx={{ 
              width: 28, 
              height: 28, 
              borderRadius: 1, 
              border: '1px solid #eee', 
              background: '#f3f5f6',
              marginRight: 0.5,
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            -
          </IconButton>
          <Typography sx={{ minWidth: 18, textAlign: 'center' }}>
            {carryOnCount}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setCarryOnCount(Math.min(1, carryOnCount + 1))}
            disabled={carryOnCount >= 1}
            sx={{ 
              width: 28, 
              height: 28, 
              borderRadius: 1, 
              border: '1px solid #eee', 
              background: carryOnCount >= 1 ? '#f0f0f0' : '#f3f5f6',
              marginLeft: 0.5,
              fontWeight: 700,
              fontSize: 18,
              color: carryOnCount >= 1 ? '#ccc' : '#000',
              cursor: carryOnCount >= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            +
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          alignContent: 'space-between',
          fontFamily: `'TT Hoves Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
          fontWeight: 400,
          fontSize: 14,
          lineHeight: '20px',
          fontFeatureSettings: '"liga" 0',
          paddingBottom: 2, 
          borderBottom: '1px solid #e0e0e0' 
        }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#222' }}>
            <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" className="c8QIE-bag-svg" role="presentation">
              <path d="M130 185a15 15 0 01-14.14-10H84.14a15 15 0 01-28.28 0H55a15 15 0 01-15-15V60a15 15 0 0115-15h15V27.12C70 22.6 73.9 15 100 15c11.2 0 30 1.58 30 12.12V45h15a15 15 0 0115 15v100a15 15 0 01-15 15h-.86A15 15 0 01130 185zm-50-20h40a5 5 0 015 5a5 5 0 0010 0a5 5 0 015-5h5a5 5 0 005-5V60a5 5 0 00-5-5H80v12.93l8.54 8.53A5 5 0 0190 80v30a5 5 0 01-5 5h-5v25a5 5 0 01-10 0v-25h-5a5 5 0 01-5-5V80a5 5 0 011.46-3.54L70 67.93V55H55a5 5 0 00-5 5v100a5 5 0 005 5h5a5 5 0 015 5a5 5 0 0010 0a5 5 0 015-5zm-10-60h10V82.07l-5-5l-5 5zm10-60h40V28.1c-2-1.23-8.93-3.1-20-3.1s-18 1.87-20 3.1zm45 100a5 5 0 01-5-5V80a5 5 0 0110 0v60a5 5 0 01-5 5z" fill="currentColor"></path>
            </svg>
            <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, lineHeight: '20px' }}>Checked bag</Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => setCheckedBagCount(Math.max(0, checkedBagCount - 1))}
            sx={{ 
              width: 28, 
              height: 28, 
              borderRadius: 1, 
              border: '1px solid #eee', 
              background: '#f3f5f6',
              marginRight: 0.5,
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            -
          </IconButton>
          <Typography sx={{ minWidth: 18, textAlign: 'center' }}>
            {checkedBagCount}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setCheckedBagCount(Math.min(2, checkedBagCount + 1))}
            disabled={checkedBagCount >= 2}
            sx={{ 
              width: 28, 
              height: 28, 
              borderRadius: 1, 
              border: '1px solid #eee', 
              background: checkedBagCount >= 2 ? '#f0f0f0' : '#f3f5f6',
              marginLeft: 0.5,
              fontWeight: 700,
              fontSize: 18,
              color: checkedBagCount >= 2 ? '#ccc' : '#000',
              cursor: checkedBagCount >= 2 ? 'not-allowed' : 'pointer',
            }}
          >
            +
          </IconButton>
        </Box>

       
      </Box>

      {/* Book on KAYAK */}
      <Box sx={{ marginBottom: 2  , paddingBottom: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 13 }}>
            Book on 
            <Typography component="span" sx={{  fontWeight: 600 , fontSize: 14 }}>
              {' '}KAYAK{' '}
            </Typography>
            <svg 
              viewBox="0 0 200 200" 
              width="1.25em" 
              height="1.25em" 
              xmlns="http://www.w3.org/2000/svg" 
              className="w_TN-whisky-icon" 
              role="presentation"
              style={{
                margin: '-2px 0',
                height: '20px',
                width: '20px',
                borderRadius: '4px',
                backgroundColor: '#ffece0',
                fill: '#f45500'
              }}
            >
              <path d="M120.239 20h0a7.998 7.998 0 0 0-6.56 3.44l-61.678 89.198H86.88l-14.8 57.359a8 8 0 0 0 8 10h0a7.998 7.998 0 0 0 6.56-3.44l61.358-89.198H113.12L127.919 30a8 8 0 0 0-7.68-10z"></path>
            </svg>
          </Typography>
          <IconButton size="small" sx={{ fontSize: 18, transform: 'rotate(90deg)', color: '#888' }}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontSize: 13 , fontWeight: 300 }}>
            Show offers instantly bookable on KAYAK.
          </Typography>
          <Switch 
            checked={bookOnKayak}
            onChange={(e) => setBookOnKayak(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase': {
                color: ' #fff',
              },
              '& .MuiSwitch-track': {
                backgroundColor: ' #ccc',
              },
              '& .Mui-checked': {
                color: '#008a00',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#008a00',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Times */}
      <Box sx={{ marginBottom: 2 , paddingBottom: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Times
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#008cff', 
              fontSize: 12, 
              cursor: 'pointer',
              fontWeight: 400,
            }}
            onClick={resetTimeFilters}
          >
            Reset
          </Typography>
        </Box>
        
        {tripType !== 'One-way' && (
          <Box sx={{ 
            display: 'flex',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            backgroundColor: '#f9fafb',
            padding: '4px',
            marginBottom: 2,
            width: '100%'
          }}>
            <Box 
              onClick={() => setTimeFilter('takeoff')}
              sx={{ 
                flex: 1,
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 14,
                backgroundColor: timeFilter === 'takeoff' ? '#fff' : 'transparent',
                color: timeFilter === 'takeoff' ? '#222' : '#666',
                boxShadow: timeFilter === 'takeoff' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              Take-off
            </Box>
            <Box 
              onClick={() => setTimeFilter('landing')}
              sx={{ 
                flex: 1,
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 14,
                backgroundColor: timeFilter === 'landing' ? '#fff' : 'transparent',
                color: timeFilter === 'landing' ? '#222' : '#666',
                boxShadow: timeFilter === 'landing' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              Landing
            </Box>
          </Box>
        )}
        
        <TimeRangeSlider
          label="Take-off from ATL"
          startTime={timeRanges.atl.start}
          endTime={timeRanges.atl.end}
          onTimeChange={handleATLTimeChange}
          minTime={6}
          maxTime={22}
        />
        
        <TimeRangeSlider
          label="Take-off from ABQ"
          startTime={timeRanges.abq.start}
          endTime={timeRanges.abq.end}
          onTimeChange={handleABQTimeChange}
          minTime={6}
          maxTime={22}
        />
      </Box>

      {/* Airlines */}
      <div style={ {marginBottom: 12}}>
        <div style={headerStyle}>
          <h6 style={titleStyle}>
            Airlines
          </h6>
          <button
            style={resetStyle}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            onClick={resetAirlinesFilter}
          >
            Reset
          </button>
        </div>

        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <Typography 
            component="span" 
            sx={{ color: '#008cff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
            onClick={selectAllAirlines}
          >
            Select all
          </Typography>
          <Typography component="span" sx={{ color: '#888', margin: '0 8px', fontSize: 13 }}>|</Typography>
          <Typography 
            component="span" 
            sx={{ color: '#008cff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
            onClick={clearAllAirlines}
          >
            Clear all
          </Typography>
        </div>

        {availableFilters.airlines.slice(0, showMoreAirlines ? undefined : 5).map(airline => {
          const isChecked = airlinesFilter.includes(airline);
          const airlineName = getAirlineName(airline);
          const minPrice = getAirlineMinTotalPrice(airline);
          
          return (
            <FilterRow
              key={airline}
              checked={isChecked}
              onToggle={() => handleAirlinesFilter(airline)}
              onOnly={() => setAirlinesFilter([airline])}
              price={typeof minPrice === 'number' ? minPrice : null}
              textStyle={{ fontSize: 14.5, fontWeight: 400 }}
              rowStyle={{ marginBottom: 6 }}
            >
              {airlineName}
            </FilterRow>
          );
        })}

        {/* Optional disabled row for Multiple airlines, matches screenshot styling */}
        {hasMultipleAirlinesOption && (
          <FilterRow
            checked={false}
            disabled
            onToggle={() => {}}
            hideOnly
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Multiple airlines
              <IconButton 
                size="small" 
                sx={{ padding: 0, marginLeft: 0.5, '&:hover': { background: 'transparent' } }}
              >
                <svg viewBox="0 0 200 200" width="14" height="14" xmlns="http://www.w3.org/2000/svg" role="presentation">
                  <path fillRule="evenodd" clipRule="evenodd" d="M100 19c-44.187 0-80 35.813-80 80s35.813 80 80 80s80-35.813 80-80s-35.813-80-80-80zm0 145.455c-36.092 0-65.454-29.363-65.454-65.455S63.908 33.545 100 33.545S165.454 62.909 165.454 99S136.092 164.455 100 164.455zm-8.98-72.284h17.96v55.419H91.02V92.171zm0-35.265h17.96v17.96H91.02v-17.96z" fill="#bbb"/>
                </svg>
              </IconButton>
            </span>
          </FilterRow>
        )}
        
        {availableFilters.airlines.length > 5 && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#008cff', 
              fontSize: 13, 
              cursor: 'pointer',
              fontWeight: 700,
              marginTop: 1
            }}
            onClick={() => setShowMoreAirlines(!showMoreAirlines)}
          >
            {showMoreAirlines ? 'Show less' : `Show ${availableFilters.airlines.length - 5} more airlines`}
          </Typography>
        )}
      </div>

      {/* Alliance */}
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h6 style={titleStyle}>
            Alliance
          </h6>
         
        </div>

        <FilterRow
          checked={allianceFilter.includes('oneworld')}
          onToggle={() => {
            if (allianceFilter.includes('oneworld')) {
              setAllianceFilter(allianceFilter.filter(item => item !== 'oneworld'));
            } else {
              setAllianceFilter([...allianceFilter, 'oneworld']);
            }
          }}
          onOnly={() => setAllianceFilter(['oneworld'])}
          price={637}
          textStyle={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}
          showInlineOnly
        >
          oneworld
        </FilterRow>

        <FilterRow
          checked={allianceFilter.includes('skyteam')}
          onToggle={() => {
            if (allianceFilter.includes('skyteam')) {
              setAllianceFilter(allianceFilter.filter(item => item !== 'skyteam'));
            } else {
              setAllianceFilter([...allianceFilter, 'skyteam']);
            }
          }}
          onOnly={() => setAllianceFilter(['skyteam'])}
          price={693}
          textStyle={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}
          showInlineOnly
        >
          SkyTeam
        </FilterRow>

        <FilterRow
          checked={allianceFilter.includes('staralliance')}
          onToggle={() => {
            if (allianceFilter.includes('staralliance')) {
              setAllianceFilter(allianceFilter.filter(item => item !== 'staralliance'));
            } else {
              setAllianceFilter([...allianceFilter, 'staralliance']);
            }
          }}
          onOnly={() => setAllianceFilter(['staralliance'])}
          price={654}
          textStyle={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}
          showInlineOnly
        >
          Star Alliance
        </FilterRow>
      </div>

     

      {/* Duration */}
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h6 style={titleStyle}>
            Duration
          </h6>
          <button
            style={resetStyle}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            onClick={resetDurationFilters}
          >
            Reset
          </button>
        </div>

        <DurationRangeSlider
          label="Flight leg"
          startDuration={durationRanges.flightLeg.start}
          endDuration={durationRanges.flightLeg.end}
          onDurationChange={handleFlightLegDurationChange}
          minDuration={80} // ~1h20m like screenshot
          maxDuration={1677} // 27h 57m like screenshot
          step={1}
          mode="max"
        />
        
        <DurationRangeSlider
          label="Layover"
          startDuration={durationRanges.layover.start}
          endDuration={durationRanges.layover.end}
          onDurationChange={handleLayoverDurationChange}
          minDuration={35}
          maxDuration={1424} // 23h 44m
          step={1}
        />
      </div>

      {/* Price Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
        marginTop: 7,
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsPriceOpen(!isPriceOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Price
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isPriceOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isPriceOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>
            <PriceRangeSlider
              minPrice={priceRange.min}
              maxPrice={priceRange.max}
              onPriceChange={handlePriceRangeChange}
              minRange={availableFilters.priceRange?.min || 0}
              maxRange={availableFilters.priceRange?.max || 10000}
              step={50}
            />
          </Box>
        )}
      </Box>

      {/* Cabin Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsCabinOpen(!isCabinOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Cabin
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isCabinOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isCabinOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>
            {[
              { cabin: 'Economy', price: 120 },
              { cabin: 'Prem Econ', price: 296 },
              { cabin: 'Business', price: 1474 },
              { cabin: 'First', price: 599 },
              { cabin: 'Mixed', price: 318 }
            ].map(({ cabin, price }) => {
              const isChecked = cabinFilter.includes(cabin);
              return (
                <FilterRow
                  key={cabin}
                  checked={isChecked}
                  onToggle={() => handleCabinFilter(cabin)}
                  onOnly={() => setCabinFilter([cabin])}
                  price={price}
                  textStyle={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}
                  showInlineOnly
                >
                  {cabin}
                </FilterRow>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Layover airports Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsLayoverAirportsOpen(!isLayoverAirportsOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Layover airports
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isLayoverAirportsOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isLayoverAirportsOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>
            {availableFilters.layoverAirports && availableFilters.layoverAirports.length > 0 ? (
              availableFilters.layoverAirports.map(airportCode => {
                const airportInfo = getLayoverAirportInfo(airportCode);
                const isChecked = layoverAirportsFilter.includes(airportCode);
                return (
                  <FilterRow
                    key={airportCode}
                    checked={isChecked}
                    onToggle={() => handleLayoverAirportsFilter(airportCode)}
                    onOnly={() => setLayoverAirportsFilter([airportCode])}
                  >
                    {airportInfo.displayName}
                  </FilterRow>
                );
              })
            ) : (
              <Typography variant="body2" sx={{ color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                No layover airports available in current search results
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Flight quality Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsFlightQualityOpen(!isFlightQualityOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Flight quality
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isFlightQualityOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isFlightQualityOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={!!flightQuality.wifiOnly}
                onChange={() => setFlightQuality({ ...flightQuality, wifiOnly: !flightQuality.wifiOnly })}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Show Wi‑Fi flights only</span>
              </div>
            </label>

            {/* Hacker Fares not supported in dataset; display disabled */}
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={false}
                onChange={() => {}}
                disabled
              />
              <div style={labelContentStyle}>
                <span style={{ ...labelTextStyle, color: '#bbb' }}>Show Hacker Fares¹</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={!!flightQuality.redEyeOnly}
                onChange={() => setFlightQuality({ ...flightQuality, redEyeOnly: !flightQuality.redEyeOnly })}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Show red‑eyes</span>
              </div>
            </label>

            {/* Longer flights sample item disabled */}
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={false}
                onChange={() => {}}
                disabled
              />
              <div style={labelContentStyle}>
                <span style={{ ...labelTextStyle, color: '#bbb' }}>Show 530 longer flights</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={!!flightQuality.layoverTransfersOnly}
                onChange={() => setFlightQuality({ ...flightQuality, layoverTransfersOnly: !flightQuality.layoverTransfersOnly })}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Show flights with layover transfers</span>
              </div>
            </label>

            {/* self-transfers not modeled separately; disable */}
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={false}
                onChange={() => {}}
                disabled
              />
              <div style={labelContentStyle}>
                <span style={{ ...labelTextStyle, color: '#bbb' }}>Show flights with self‑transfers</span>
              </div>
            </label>
          </Box>
        )}
      </Box>

      {/* Aircraft Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsAircraftOpen(!isAircraftOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Aircraft
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isAircraftOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isAircraftOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>

           
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Narrow-body jet</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={false}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={{...labelTextStyle, color: '#bbb'}}>Other</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Regional jet</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Turbo-prop plane</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Wide-body jet</span>
              </div>
            </label>

            {/* Model Section */}
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222', marginTop: 2, marginBottom: 1 }}>
              Model
            </Typography>
            
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Airbus A220</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Airbus A320neo</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Boeing 737</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Boeing 737 MAX 8</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Boeing 737 MAX 9</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Boeing 777</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Other</span>
              </div>
            </label>
          </Box>
        )}
      </Box>

      {/* Booking sites Accordion */}
      <Box sx={{ 
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '12px 0',
            cursor: 'pointer'
          }}
          onClick={() => setIsBookingSitesOpen(!isBookingSitesOpen)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#222' }}>
            Booking sites
          </Typography>
          <IconButton 
            size="small" 
            sx={{ 
              padding: 0, 
              '&:hover': { background: 'transparent' }
            }}
          >
            <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
              <path d={isBookingSitesOpen ? "M100 67.5c3.873 0-.136-2.376 64.801 51.738l-9.603 11.523L100 84.763l-55.199 45.999l-9.603-11.523C100.194 65.076 96.145 67.5 100 67.5z" : "M100 132.5c-3.873 0 .136 2.376-64.801-51.738l9.603-11.523L100 115.237l55.199-45.999l9.603 11.523C99.806 134.924 103.855 132.5 100 132.5z"} fill="#666"/>
            </svg>
          </IconButton>
        </Box>
        
        {/* Expanded Content */}
        {isBookingSitesOpen && (
          <Box sx={{ padding: '0 0 16px 0' }}>
            <label style={labelStyle}>
              <CustomCheckbox 
                checked={false}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Airlines only</span>
                <IconButton 
                  size="small" 
                  sx={{ 
                    padding: 0, 
                    marginLeft: 0.5,
                    '&:hover': { background: 'transparent' }
                  }}
                >
                  <svg viewBox="0 0 200 200" width="16px" height="16px" xmlns="http://www.w3.org/2000/svg" role="presentation">
                    <path fillRule="evenodd" clipRule="evenodd" d="M100 19c-44.187 0-80 35.813-80 80s35.813 80 80 80s80-35.813 80-80s-35.813-80-80-80zm0 145.455c-36.092 0-65.454-29.363-65.454-65.455S63.908 33.545 100 33.545S165.454 62.909 165.454 99S136.092 164.455 100 164.455zm-8.98-72.284h17.96v55.419H91.02V92.171zm0-35.265h17.96v17.96H91.02v-17.96z" fill="#666"/>
                  </svg>
                </IconButton>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Alaska Airlines</span>
                <span style={priceStyle}>$1,303</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>American Airlines</span>
                <span style={priceStyle}>$907</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Delta</span>
                <span style={priceStyle}>$449</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Expedia</span>
                <span style={priceStyle}>$246</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>JetBlue</span>
                <span style={priceStyle}>$569</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Mytrip <span style={{ textDecoration: 'underline' }}>only</span></span>
                <span style={priceStyle}>$573</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>Southwest</span>
                <span style={priceStyle}>$769</span>
              </div>
            </label>

            <label style={labelStyle}>
              <CustomCheckbox 
                checked={true}
                onChange={() => {}}
              />
              <div style={labelContentStyle}>
                <span style={labelTextStyle}>United</span>
                <span style={priceStyle}>$360</span>
              </div>
            </label>

            <Typography 
              variant="body2" 
              sx={{ 
                color: '#008cff', 
                fontSize: 13, 
                cursor: 'pointer',
                fontWeight: 700,
                marginTop: 1
              }}
            >
              Show less
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default FiltersSidebar;